import { FC, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { createNewScenario, updateScenario } from 'RestAPIs/scenario';

import { planUnitsAtom } from '../../atoms';

import {
  Checkbox,
  Chip,
  DialogActions,
  FormControlLabel,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import { LoadingOverlay, showSnackbar } from 'components/shared';

import getScenarioSchema from './schema';
import { formatScenarioFormDataForSave } from './util';
import type { ScenarioForm } from './types';
import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';
import {
  GridContainer,
  GridCompact12
} from 'components/shared/Grid/Grid.components';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  onSave: (scenarioId: number) => void;
  scenario: ScenarioForm | null;
  scenarioToCopyId: number | null;
};

const defaultFormValues = {
  name: '',
  year: new Date().getFullYear(),
  costCenters: [],
  scenarioToCopyId: null,
  note: '',
  isDefault: false
};

const AddEditScenarioDialog: FC<Props> = ({
  isOpen,
  handleClose: _handleClose,
  onSave,
  scenario,
  scenarioToCopyId
}) => {
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);
  const defaultValues = useMemo(
    () => (scenario ? { ...scenario } : defaultFormValues),
    [scenario]
  );

  const { control, handleSubmit, reset, setValue } = useForm<ScenarioForm>({
    defaultValues,
    resolver: yupResolver(getScenarioSchema())
  });

  useEffect(() => {
    if (!scenario) {
      setValue('costCenters', selectedPlanUnits);
    }
  }, [scenario, setValue, selectedPlanUnits]);

  const handleClose = () => {
    reset();
    _handleClose();
  };

  const onSubmit: SubmitHandler<ScenarioForm> = async (data) => {
    const payload = formatScenarioFormDataForSave(data, scenarioToCopyId);

    setIsLoading(true);

    const handleSuccess = () => {
      showSnackbar({
        severity: 'success',
        message: t('scenario.saved')
      });
      handleClose();
    };

    if (scenario && scenario.scenarioId) {
      updateScenario(payload, scenario.scenarioId)
        .then(() => {
          onSave(scenario.scenarioId as number);
          handleSuccess();
        })
        .catch(() => {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
          setIsLoading(false);
        });
    } else {
      createNewScenario(payload)
        .then(({ data }) => {
          onSave(data.scenarioId);
          handleSuccess();
        })
        .catch((error) => {
          const message = t(
            error?.response?.data?.error?.message || 'somethingWentWrong'
          );
          showSnackbar({
            severity: 'error',
            message: message
          });
          setIsLoading(false);
        });
    }
  };

  const renderPlanUnitChips = () => {
    const costCenters = scenario ? scenario.costCenters : selectedPlanUnits;

    return costCenters.map((planUnit) => (
      <Grid key={planUnit?.id}>
        <Chip label={planUnit?.name} />
      </Grid>
    ));
  };

  return (
    <StyledDialogMedium
      open={isOpen}
      title={scenario ? t('scenario.edit') : t('scenario.add')}
      handleClose={handleClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <GridContainer>
          <GridCompact12>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('scenario.name')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  fullWidth
                  required
                />
              )}
            />
          </GridCompact12>
        </GridContainer>
        <GridContainer mt={1}>
          <GridCompact12>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('planUnits')}
            </Typography>
            <GridContainer>{renderPlanUnitChips()}</GridContainer>
          </GridCompact12>
        </GridContainer>
        <GridContainer mt={1}>
          <GridCompact12>
            <Controller
              name="isDefault"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  }
                  label={t('scenario.isDefault') as string}
                />
              )}
            />
          </GridCompact12>
        </GridContainer>
        <GridContainer mt={1}>
          <GridCompact12>
            <Controller
              name="note"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  fullWidth
                  {...field}
                  label={t('scenario.note')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? null}
                  multiline
                  minRows={3}
                />
              )}
            />
          </GridCompact12>
        </GridContainer>
        <DialogActions sx={{ mt: 3 }}>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {t('save')}
          </Button>
        </DialogActions>
      </form>
      <LoadingOverlay loading={isLoading} />
    </StyledDialogMedium>
  );
};

export default AddEditScenarioDialog;
